import { Link, useParams } from "react-router-dom";
import { useState, useEffect, useMemo } from "react";
import { ArrowLeft, Calendar, Clock } from "lucide-react";
import { api } from "@/utils/api-client";
import { LoadingState } from "@/components/LoadingState";

type BlogPostData = {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  content: string;
  coverImage: string;
  author: string;
  tags: string[];
  publishedAt: string;
  createdAt: string;
};

function estimateReadTime(content: string): number {
  return Math.max(1, Math.ceil(content.split(/\s+/).length / 200));
}

function renderInline(text: string): React.ReactNode {
  const parts: React.ReactNode[] = [];
  let remaining = text;
  let key = 0;

  while (remaining.length > 0) {
    const boldMatch = remaining.match(/\*\*(.+?)\*\*/);
    const linkMatch = remaining.match(/\[([^\]]+)\]\(([^)]+)\)/);
    const codeMatch = remaining.match(/`([^`]+)`/);

    const matches = [
      boldMatch ? { type: "bold", match: boldMatch, index: boldMatch.index! } : null,
      linkMatch ? { type: "link", match: linkMatch, index: linkMatch.index! } : null,
      codeMatch ? { type: "code", match: codeMatch, index: codeMatch.index! } : null,
    ].filter(Boolean).sort((a, b) => a!.index - b!.index);

    if (matches.length === 0) { parts.push(remaining); break; }
    const e = matches[0]!;
    if (e.index > 0) parts.push(remaining.substring(0, e.index));

    if (e.type === "bold") parts.push(<strong key={key++} className="font-semibold text-foreground">{e.match![1]}</strong>);
    else if (e.type === "link") parts.push(<a key={key++} href={e.match![2]} target="_blank" rel="noopener noreferrer" className="text-primary underline hover:opacity-80">{e.match![1]}</a>);
    else if (e.type === "code") parts.push(<code key={key++} className="rounded bg-muted px-1 py-0.5 font-mono text-[0.85em] text-primary">{e.match![1]}</code>);

    remaining = remaining.substring(e.index + e.match![0].length);
  }
  return parts.length === 1 && typeof parts[0] === "string" ? parts[0] : <>{parts}</>;
}

function renderContent(content: string) {
  return content.split("\n\n").map((block, i) => {
    if (block.startsWith("### ")) return <h3 key={i} className="mb-2 mt-8 font-display text-lg font-semibold text-foreground">{renderInline(block.slice(4))}</h3>;
    if (block.startsWith("## ")) return <h2 key={i} className="mb-3 mt-10 font-display text-xl font-semibold text-foreground">{renderInline(block.slice(3))}</h2>;
    if (block.startsWith("# ")) return <h1 key={i} className="mb-4 mt-10 font-display text-2xl font-semibold text-foreground">{renderInline(block.slice(2))}</h1>;
    if (block.startsWith("> ")) return <blockquote key={i} className="my-5 border-l-2 border-primary/50 pl-4 italic text-muted-foreground">{renderInline(block.replace(/^> /gm, ""))}</blockquote>;
    if (block.startsWith("- ")) return <ul key={i} className="mb-4 list-disc space-y-1.5 pl-5 leading-relaxed text-muted-foreground">{block.split("\n").filter(Boolean).map((l, j) => <li key={j}>{renderInline(l.slice(2))}</li>)}</ul>;
    if (block.startsWith("```")) { const code = block.replace(/^```[a-z]*\n?/, "").replace(/```$/, "").trim(); return <pre key={i} className="my-5 overflow-x-auto rounded-xl border border-border bg-background/60 p-4 font-mono text-sm leading-relaxed text-primary"><code>{code}</code></pre>; }
    if (block.trim() === "---") return <hr key={i} className="my-8 border-border" />;
    return <p key={i} className="mt-5 text-base leading-[1.8] text-foreground/90">{renderInline(block)}</p>;
  });
}

export default function BlogPostPage() {
  const { slug } = useParams();
  const [post, setPost] = useState<BlogPostData | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!slug) return;
    api.get<Record<string, unknown>>(`/api/public/blog/${slug}`)
      .then((data) => { if (!data) setNotFound(true); else setPost({ ...data, id: (data.id as string) || String(data._id) } as BlogPostData); })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  const readTime = useMemo(() => (post ? estimateReadTime(post.content) : 0), [post]);

  if (notFound) {
    return (
      <div className="grid min-h-[60vh] place-items-center px-4 pt-32 text-center">
        <div>
          <h1 className="font-display text-2xl font-semibold">Post not found</h1>
          <p className="mt-2 text-sm text-muted-foreground">This article does not exist or has been moved.</p>
          <Link to="/blog" className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-primary hover:underline">
            <ArrowLeft className="h-4 w-4" /> Back to blog
          </Link>
        </div>
      </div>
    );
  }

  return (
    <article className="pb-24 pt-32 sm:pt-40">
      <div className="mx-auto max-w-3xl px-4 sm:px-6">
        {loading && <LoadingState loading error={null} variant="inline">{null}</LoadingState>}
        {!loading && post && (
          <>
            <Link to="/blog" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground">
              <ArrowLeft className="h-4 w-4" /> All posts
            </Link>

            {post.tags?.[0] && <div className="mt-8 text-xs uppercase tracking-[0.18em] text-primary">{post.tags[0]}</div>}

            <h1 className="mt-3 font-display text-4xl font-semibold leading-[1.1] tracking-tight sm:text-5xl">{post.title}</h1>

            <div className="mt-5 flex items-center gap-4 border-b border-border pb-6 text-sm text-muted-foreground">
              {post.publishedAt && (
                <span className="flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5" />{new Date(post.publishedAt).toLocaleDateString("en-IN", { month: "long", day: "numeric", year: "numeric" })}</span>
              )}
              <span className="flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" />{readTime} min read</span>
            </div>

            {post.coverImage && (
              <div
                className="my-10 aspect-[16/8] rounded-2xl border border-border"
                style={{ backgroundImage: `url(${post.coverImage})`, backgroundSize: "cover", backgroundPosition: "center" }}
              />
            )}

            <div className="mt-8">{renderContent(post.content)}</div>

            {post.tags?.length > 0 && (
              <div className="mt-14 flex flex-wrap gap-2 border-t border-border pt-8">
                {post.tags.map((t) => (
                  <span key={t} className="rounded-full border border-border px-2.5 py-1 text-xs text-muted-foreground">#{t}</span>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </article>
  );
}
